/**
 * Instructions Modal Component
 * Displays user guide with navigation
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Divider,
  Paper,
} from '@mui/material';
import { Close as CloseIcon, Help as HelpIcon } from '@mui/icons-material';
// Simple markdown parser for basic formatting

interface Instruction {
  id: string;
  title: string;
  file: string;
}

const instructions: Instruction[] = [
  { id: 'authentication', title: 'Authentication and Registration', file: '01-authentication.md' },
  { id: 'user-roles', title: 'User Roles', file: '02-user-roles.md' },
  { id: 'visibility-model', title: 'Visibility Model', file: '03-visibility-model.md' },
  { id: 'creating-scenarios', title: 'Creating Scenarios', file: '04-creating-scenarios.md' },
  { id: 'reservoir-templates', title: 'Reservoir Templates', file: '05-reservoir-templates.md' },
  { id: 'running-calculations', title: 'Running Calculations', file: '06-running-calculations.md' },
  { id: 'viewing-results', title: 'Viewing Results', file: '07-viewing-results.md' },
  { id: 'contacts', title: 'Contacts and Support', file: '08-contacts.md' },
  { id: 'development-roadmap', title: 'Development Roadmap', file: '09-development-roadmap.md' },
];

interface InstructionsModalProps {
  open: boolean;
  onClose: () => void;
}

const PAPER_WIDTH_PX = 960;
const PAPER_MIN_W = 360;
const PAPER_MIN_H = 260;
/** Hit area thickness for edge drags (px) */
const RESIZE_EDGE = 8;
/** Corner grab size (px) */
const RESIZE_CORNER = 14;

type ResizeMode = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

function initialPaperSize(): { w: number; h: number } {
  if (typeof window === 'undefined') {
    return { w: PAPER_WIDTH_PX, h: 700 };
  }
  return {
    w: Math.min(PAPER_WIDTH_PX, window.innerWidth - 24),
    h: Math.min(Math.round(window.innerHeight * 0.88), window.innerHeight - 24),
  };
}

export const InstructionsModal: React.FC<InstructionsModalProps> = ({ open, onClose }) => {
  const [selectedInstruction, setSelectedInstruction] = useState<string>(instructions[0]?.id ?? 'authentication');
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [paperSize, setPaperSize] = useState<{ w: number; h: number }>(initialPaperSize);
  const [paperPos, setPaperPos] = useState<{ x: number; y: number }>(() => {
    const sz = initialPaperSize();
    const ww = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const wh = typeof window !== 'undefined' ? window.innerHeight : 800;
    return {
      x: Math.max(16, ww / 2 - sz.w / 2),
      y: Math.max(16, wh * 0.06),
    };
  });
  const paperRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{
    active: boolean;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
  } | null>(null);
  const resizeRef = useRef<{
    active: boolean;
    mode: ResizeMode;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null>(null);

  const clampPosition = useCallback(
    (x: number, y: number) => {
      const w = paperRef.current?.offsetWidth ?? paperSize.w;
      const h = paperRef.current?.offsetHeight ?? paperSize.h;
      const maxX = Math.max(8, window.innerWidth - w - 8);
      const maxY = Math.max(8, window.innerHeight - h - 8);
      return {
        x: Math.min(maxX, Math.max(8, x)),
        y: Math.min(maxY, Math.max(8, y)),
      };
    },
    [paperSize.w, paperSize.h]
  );

  const handleGuideHeaderMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) {
      return;
    }
    e.preventDefault();
    dragRef.current = {
      active: true,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: paperPos.x,
      startY: paperPos.y,
    };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.active) return;
      const nx = d.startX + (ev.clientX - d.startClientX);
      const ny = d.startY + (ev.clientY - d.startClientY);
      setPaperPos(clampPosition(nx, ny));
    };
    const onUp = () => {
      if (dragRef.current) {
        dragRef.current.active = false;
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const handleResizeMouseDown = (e: React.MouseEvent, mode: ResizeMode) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      active: true,
      mode,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: paperPos.x,
      startY: paperPos.y,
      startW: paperSize.w,
      startH: paperSize.h,
    };
    const onMove = (ev: MouseEvent) => {
      const d = resizeRef.current;
      if (!d?.active) return;
      const dx = ev.clientX - d.startClientX;
      const dy = ev.clientY - d.startClientY;
      let x = d.startX;
      let y = d.startY;
      let w = d.startW;
      let h = d.startH;

      switch (d.mode) {
        case 'se':
          w = d.startW + dx;
          h = d.startH + dy;
          break;
        case 'e':
          w = d.startW + dx;
          break;
        case 's':
          h = d.startH + dy;
          break;
        case 'w':
          x = d.startX + dx;
          w = d.startW - dx;
          break;
        case 'n':
          y = d.startY + dy;
          h = d.startH - dy;
          break;
        case 'ne':
          y = d.startY + dy;
          h = d.startH - dy;
          w = d.startW + dx;
          break;
        case 'nw':
          x = d.startX + dx;
          w = d.startW - dx;
          y = d.startY + dy;
          h = d.startH - dy;
          break;
        case 'sw':
          x = d.startX + dx;
          w = d.startW - dx;
          h = d.startH + dy;
          break;
        default:
          return;
      }

      w = Math.max(PAPER_MIN_W, w);
      h = Math.max(PAPER_MIN_H, h);
      w = Math.min(w, window.innerWidth - x - 8);
      h = Math.min(h, window.innerHeight - y - 8);
      x = Math.max(8, Math.min(x, window.innerWidth - w - 8));
      y = Math.max(8, Math.min(y, window.innerHeight - h - 8));

      setPaperPos({ x, y });
      setPaperSize({ w, h });
    };
    const onUp = () => {
      if (resizeRef.current) {
        resizeRef.current.active = false;
      }
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  useEffect(() => {
    if (open && selectedInstruction) {
      loadInstruction(selectedInstruction);
    }
  }, [open, selectedInstruction]);

  const parseInlineMarkdown = (text: string): React.ReactNode[] => {
    if (!text) return [text];

    const parts: React.ReactNode[] = [];

    // Pattern for **bold** (non-greedy)
    const boldPattern = /\*\*([^*]+?)\*\*/g;
    // Pattern for `code`
    const codePattern = /`([^`]+?)`/g;

    const matches: Array<{ start: number; end: number; type: 'bold' | 'code'; content: string }> = [];

    // Find all bold matches
    let match;
    boldPattern.lastIndex = 0;
    while ((match = boldPattern.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'bold',
        content: match[1]
      });
    }

    // Find all code matches
    codePattern.lastIndex = 0;
    while ((match = codePattern.exec(text)) !== null) {
      matches.push({
        start: match.index,
        end: match.index + match[0].length,
        type: 'code',
        content: match[1]
      });
    }

    // Sort matches by start position
    matches.sort((a, b) => a.start - b.start);

    // Remove overlapping matches (keep first match)
    const filteredMatches: typeof matches = [];
    for (let i = 0; i < matches.length; i++) {
      const current = matches[i];
      let overlaps = false;
      for (let j = 0; j < filteredMatches.length; j++) {
        const existing = filteredMatches[j];
        if (
          (current.start < existing.end && current.end > existing.start)
        ) {
          overlaps = true;
          break;
        }
      }
      if (!overlaps) {
        filteredMatches.push(current);
      }
    }

    // Build React nodes
    let lastIndex = 0;
    filteredMatches.forEach((match, idx) => {
      // Add text before match
      if (match.start > lastIndex) {
        const beforeText = text.substring(lastIndex, match.start);
        if (beforeText) {
          parts.push(beforeText);
        }
      }

      // Add formatted match
      if (match.type === 'bold') {
        parts.push(<strong key={`bold-${idx}`}>{match.content}</strong>);
      } else if (match.type === 'code') {
        parts.push(
          <Box
            key={`code-${idx}`}
            component="span"
            sx={{
              backgroundColor: 'grey.100',
              padding: '2px 4px',
              borderRadius: 1,
              fontSize: '0.9em',
              fontFamily: 'monospace',
            }}
          >
            {match.content}
          </Box>
        );
      }

      lastIndex = match.end;
    });

    // Add remaining text
    if (lastIndex < text.length) {
      const remainingText = text.substring(lastIndex);
      if (remainingText) {
        parts.push(remainingText);
      }
    }

    return parts.length > 0 ? parts : [text];
  };

  const parseMarkdown = (text: string): React.ReactNode[] => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let currentParagraph: string[] = [];
    let inCodeBlock = false;
    let codeBlockContent: string[] = [];
    let inList = false;
    let listItems: string[] = [];

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        const paragraphText = currentParagraph.join(' ');
        const parsedContent = parseInlineMarkdown(paragraphText);
        elements.push(
          <Typography key={`p-${elements.length}`} paragraph>
            {parsedContent}
          </Typography>
        );
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(
          <Box key={`list-${elements.length}`} component="ul" sx={{ pl: 3, mb: 1.5 }}>
            {listItems.map((item, idx) => {
              const parsedItem = parseInlineMarkdown(item);
              return (
                <Typography key={idx} component="li" sx={{ mb: 0.5 }}>
                  {parsedItem}
                </Typography>
              );
            })}
          </Box>
        );
        listItems = [];
        inList = false;
      }
    };

    lines.forEach((line) => {
      if (line.startsWith('```')) {
        flushList();
        flushParagraph();
        if (inCodeBlock) {
          elements.push(
            <Box
              key={`code-${elements.length}`}
              component="pre"
              sx={{
                backgroundColor: 'grey.100',
                p: 2,
                borderRadius: 1,
                overflow: 'auto',
                mb: 2,
                fontFamily: 'monospace',
                fontSize: '0.9rem',
              }}
            >
              {codeBlockContent.join('\n')}
            </Box>
          );
          codeBlockContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlockContent.push(line);
        return;
      }

      if (line.trim() === '') {
        flushList();
        flushParagraph();
        return;
      }

      if (line.startsWith('# ')) {
        flushList();
        flushParagraph();
        const headingText = line.substring(2).trim();
        const parsedHeading = parseInlineMarkdown(headingText);
        elements.push(
          <Typography key={`h1-${elements.length}`} variant="h4" component="h1" gutterBottom sx={{ mt: 0, mb: 2 }}>
            {parsedHeading}
          </Typography>
        );
      } else if (line.startsWith('## ')) {
        flushList();
        flushParagraph();
        const headingText = line.substring(3).trim();
        const parsedHeading = parseInlineMarkdown(headingText);
        elements.push(
          <Typography key={`h2-${elements.length}`} variant="h5" component="h2" gutterBottom sx={{ mt: 3, mb: 1.5 }}>
            {parsedHeading}
          </Typography>
        );
      } else if (line.startsWith('### ')) {
        flushList();
        flushParagraph();
        const headingText = line.substring(4).trim();
        const parsedHeading = parseInlineMarkdown(headingText);
        elements.push(
          <Typography key={`h3-${elements.length}`} variant="h6" component="h3" gutterBottom sx={{ mt: 2, mb: 1 }}>
            {parsedHeading}
          </Typography>
        );
      } else if (line.startsWith('- ') || line.startsWith('* ')) {
        flushParagraph();
        inList = true;
        // Remove list marker and any leading spaces
        const listItem = line.replace(/^[-*]\s+/, '').trim();
        listItems.push(listItem);
      } else if (line.match(/^\d+\.\s/)) {
        flushParagraph();
        inList = true;
        const listItem = line.replace(/^\d+\.\s+/, '').trim();
        listItems.push(listItem);
      } else if (line.match(/^\s{2,}/) && inList) {
        // Continuation of list item (indented line)
        if (listItems.length > 0) {
          listItems[listItems.length - 1] += ' ' + line.trim();
        }
      } else {
        flushList();
        currentParagraph.push(line.trim());
      }
    });

    flushList();
    flushParagraph();
    return elements;
  };

  const loadInstruction = async (instructionId: string) => {
    const instruction = instructions.find(inst => inst.id === instructionId);
    if (!instruction) return;

    try {
      setLoading(true);
      const response = await fetch(`/instructions/${instruction.file}`, {
        headers: {
          'Accept': 'text/markdown',
        },
      });
      if (response.ok) {
        const text = await response.text();
        setContent(text);
      } else {
        setContent(`# ${instruction.title}\n\nContent not available.`);
      }
    } catch (error) {
      console.error('Failed to load instruction:', error);
      setContent(`# ${instruction.title}\n\nFailed to load content. Please try again later.`);
    } finally {
      setLoading(false);
    }
  };

  const handleInstructionSelect = (instructionId: string) => {
    setSelectedInstruction(instructionId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      disableScrollLock
      sx={{
        pointerEvents: 'none',
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
        },
      }}
      PaperProps={{
        ref: paperRef,
        sx: {
          pointerEvents: 'auto',
          position: 'fixed',
          left: paperPos.x,
          top: paperPos.y,
          margin: 0,
          width: paperSize.w,
          height: paperSize.h,
          maxWidth: 'calc(100vw - 16px)',
          maxHeight: 'calc(100vh - 16px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          boxShadow: 12,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        onMouseDown={handleGuideHeaderMouseDown}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          pr: 1,
          cursor: 'grab',
          userSelect: 'none',
          flexShrink: 0,
          backgroundColor: 'primary.main',
          color: 'primary.contrastText',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpIcon sx={{ color: 'primary.contrastText' }} />
          <Typography variant="h6" component="span" sx={{ color: 'primary.contrastText' }}>
            QuickField User Guide
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close user guide"
          sx={{
            cursor: 'pointer',
            position: 'relative',
            zIndex: 10,
            color: 'primary.contrastText',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ p: 0, display: 'flex', overflow: 'hidden', flex: 1, minHeight: 0 }}>
        {/* Navigation Sidebar */}
        <Paper
          sx={{
            width: 250,
            flexShrink: 0,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
            borderRadius: 0,
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Table of Contents
            </Typography>
          </Box>
          <List dense sx={{ pt: 0 }}>
            {instructions.map((instruction) => (
              <ListItem key={instruction.id} disablePadding>
                <ListItemButton
                  selected={selectedInstruction === instruction.id}
                  onClick={() => handleInstructionSelect(instruction.id)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                      },
                    },
                  }}
                >
                  <ListItemText primary={instruction.title} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
            '& h1': {
              fontSize: '2rem',
              fontWeight: 600,
              mb: 2,
              mt: 0,
            },
            '& h2': {
              fontSize: '1.5rem',
              fontWeight: 600,
              mb: 1.5,
              mt: 3,
            },
            '& h3': {
              fontSize: '1.25rem',
              fontWeight: 600,
              mb: 1,
              mt: 2,
            },
            '& p': {
              mb: 1.5,
              lineHeight: 1.6,
            },
            '& ul, & ol': {
              mb: 1.5,
              pl: 3,
            },
            '& li': {
              mb: 0.5,
            },
            '& code': {
              backgroundColor: 'grey.100',
              padding: '2px 4px',
              borderRadius: 1,
              fontSize: '0.9em',
            },
            '& pre': {
              backgroundColor: 'grey.100',
              padding: 2,
              borderRadius: 1,
              overflow: 'auto',
              mb: 2,
            },
            '& blockquote': {
              borderLeft: 3,
              borderColor: 'primary.main',
              pl: 2,
              ml: 0,
              fontStyle: 'italic',
              color: 'text.secondary',
            },
          }}
        >
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Box component="div">
              {parseMarkdown(content)}
            </Box>
          )}
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 2, py: 1.5, position: 'relative', justifyContent: 'flex-start' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
      {/* Resize hit zones: corners above edges (z-index) */}
      <Box
        aria-hidden
        onMouseDown={(e) => handleResizeMouseDown(e, 'n')}
        sx={{
          position: 'absolute',
          zIndex: 5,
          top: 0,
          left: RESIZE_CORNER,
          right: RESIZE_CORNER,
          height: RESIZE_EDGE,
          cursor: 'ns-resize',
        }}
      />
      <Box
        aria-hidden
        onMouseDown={(e) => handleResizeMouseDown(e, 's')}
        sx={{
          position: 'absolute',
          zIndex: 5,
          bottom: 0,
          left: RESIZE_CORNER,
          right: RESIZE_CORNER,
          height: RESIZE_EDGE,
          cursor: 'ns-resize',
        }}
      />
      <Box
        aria-hidden
        onMouseDown={(e) => handleResizeMouseDown(e, 'e')}
        sx={{
          position: 'absolute',
          zIndex: 5,
          top: RESIZE_CORNER,
          bottom: RESIZE_CORNER,
          right: 0,
          width: RESIZE_EDGE,
          cursor: 'ew-resize',
        }}
      />
      <Box
        aria-hidden
        onMouseDown={(e) => handleResizeMouseDown(e, 'w')}
        sx={{
          position: 'absolute',
          zIndex: 5,
          top: RESIZE_CORNER,
          bottom: RESIZE_CORNER,
          left: 0,
          width: RESIZE_EDGE,
          cursor: 'ew-resize',
        }}
      />
      <Box
        aria-label="Resize user guide (north-west)"
        title="Resize"
        onMouseDown={(e) => handleResizeMouseDown(e, 'nw')}
        sx={{
          position: 'absolute',
          zIndex: 6,
          top: 0,
          left: 0,
          width: RESIZE_CORNER,
          height: RESIZE_CORNER,
          cursor: 'nwse-resize',
        }}
      />
      <Box
        aria-label="Resize user guide (north-east)"
        title="Resize"
        onMouseDown={(e) => handleResizeMouseDown(e, 'ne')}
        sx={{
          position: 'absolute',
          zIndex: 6,
          top: 0,
          right: 0,
          width: RESIZE_CORNER,
          height: RESIZE_CORNER,
          cursor: 'nesw-resize',
        }}
      />
      <Box
        aria-label="Resize user guide (south-west)"
        title="Resize"
        onMouseDown={(e) => handleResizeMouseDown(e, 'sw')}
        sx={{
          position: 'absolute',
          zIndex: 6,
          bottom: 0,
          left: 0,
          width: RESIZE_CORNER,
          height: RESIZE_CORNER,
          cursor: 'nesw-resize',
        }}
      />
      <Box
        aria-label="Resize user guide (south-east)"
        title="Resize"
        onMouseDown={(e) => handleResizeMouseDown(e, 'se')}
        sx={{
          position: 'absolute',
          zIndex: 6,
          bottom: 0,
          right: 0,
          width: RESIZE_CORNER,
          height: RESIZE_CORNER,
          cursor: 'nwse-resize',
          borderBottomRightRadius: 8,
          background: (theme) =>
            `linear-gradient(135deg, transparent 55%, ${theme.palette.primary.main}22 55%)`,
          '&:hover': {
            background: (theme) =>
              `linear-gradient(135deg, transparent 50%, ${theme.palette.primary.main}44 50%)`,
          },
        }}
      />
    </Dialog>
  );
};
